// === cart.js - Stable Extend Bridge (singleton), no ping-pong, synced page ↔ drawer ===

// Assume these are defined globally by the theme or other scripts.
// Fallbacks are provided at the end if not.

// --- Cart row → cart.js item helpers ---------------------------------------
function _parseKeyFromHref(href) {
  if (!href) return null;
  // /cart/change?id=<variantId>:<key>&quantity=0
  const m = href.match(/id=[^:]+:([^&?#]+)/i);
  return m ? m[1] : null;
}

function _findCartItemForRow(row, items) {
  if (!row || !Array.isArray(items)) return null;

  // Try by key (best)
  const removeHref = row.querySelector('cart-remove-button a')?.getAttribute('href') || '';
  const key = _parseKeyFromHref(removeHref);
  if (key) {
    const byKey = items.find(it => it.key === key);
    if (byKey) return byKey;
  }

  // Fallback by variant id
  const vAttr = row.querySelector('[data-quantity-variant-id]')?.getAttribute('data-quantity-variant-id');
  const variantId = vAttr ? parseInt(vAttr, 10) : NaN;
  if (!isNaN(variantId)) {
    const byVariant = items.find(it => it.variant_id === variantId);
    if (byVariant) return byVariant;
  }

  return null;
}

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartComponent = this.closest('cart-items') || this.closest('cart-drawer-items');
      if (cartComponent && typeof cartComponent.updateQuantity === 'function') {
        cartComponent.updateQuantity(this.dataset.index, 0);
      } else {
        console.error('[CartRemoveButton] CartItems/CartDrawerItems element or updateQuantity method not found.', this);
      }
    });
  }
}
customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    this._drawerListenersAdded = false;
    this._drawerOpenHandlerRef = null;
    this._drawerCloseHandlerRef = null;

    const debounceFn = typeof debounce === 'function' ? debounce : (fn, wait) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn.apply(this,a), wait);}};
    const currentOnChangeDebounceTimer = typeof ON_CHANGE_DEBOUNCE_TIMER !== 'undefined' ? ON_CHANGE_DEBOUNCE_TIMER : 300;
    const debouncedOnChange = debounceFn((event) => { this.onChange(event); }, currentOnChangeDebounceTimer);
    this.addEventListener('change', debouncedOnChange);

    this.cartDrawer = document.querySelector('cart-drawer') || document.getElementById('CartDrawer');

    // Context-aware setup
    if (typeof this._getContextIdentifier === 'function') {
      const currentContext = this._getContextIdentifier();
      if (currentContext === 'cart-context-main' && typeof this.initObserver === 'function') this.initObserver();
      if (this.cartDrawer && typeof this.addCartDrawerListeners === 'function') this.addCartDrawerListeners();
    }

    // ===== Install the global (singleton) Extend bridge ONCE =====
    if (!window.__extendBridgeInstalled) {
      window.__extendBridgeInstalled = true;
      // Guard (source + throttle)
      window.__extendBridge_lastRun = 0;
      window.__extendBridge_busy = false;

      const runBridge = (evtName, evt) => {
        // Ignore events we ourselves dispatched
        if (evt && evt.detail && evt.detail.by === 'theme') return;

        const now = Date.now();
        if (window.__extendBridge_busy) return;
        if (now - window.__extendBridge_lastRun < 250) return; // throttle bursts

        window.__extendBridge_busy = true;
        window.__extendBridge_lastRun = now;

        const cartJsonUrl = window.routes?.cart_url ? `${window.routes.cart_url}.js` : '/cart.js';
        fetch(cartJsonUrl)
          .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
          .then((freshCart) => {
            // Update visual state immediately on all instances
            const instances = Array.from(document.querySelectorAll('cart-items, cart-drawer-items'));
            instances.forEach(inst => {
              if (typeof inst.updateCartDisplayState === 'function') inst.updateCartDisplayState(freshCart);
            });
            // Render sections via each instance (they know what to update on the current page)
            return Promise.all(instances.map(inst => {
              if (typeof inst.renderSections === 'function') return inst.renderSections(freshCart);
              return Promise.resolve();
            })).then(() => freshCart);
          })
          .then((freshCart) => {
            // Theme-wide pub/sub so other listeners (e.g. header bubble) react immediately
            const currentPubSub = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : { cartUpdate: 'cart:update' };
            const THEME_CART_UPDATE_EVENT_NAME = String(currentPubSub.cartUpdate);
            document.dispatchEvent(new CustomEvent(THEME_CART_UPDATE_EVENT_NAME, { bubbles: true, detail: { cart: freshCart, cartData: freshCart, source: 'extend-bridge' } }));

            // After our sections are stable, tell Extend to (re)init in BOTH contexts.
            // Mark these as from the theme so THIS bridge ignores them (no ping-pong).
            setTimeout(() => {
              try {
                window.dispatchEvent(new CustomEvent('refreshAjaxCart', { detail: { by: 'theme', reason: 'sections-rendered' }}));
                window.dispatchEvent(new CustomEvent('refreshAjaxSideCart', { detail: { by: 'theme', reason: 'sections-rendered' }}));
              } catch (e) {
                console.warn('[ExtendBridge] Could not dispatch Extend refresh events:', e);
              }
            }, 120);
          })
          .catch((e) => console.error('[ExtendBridge] Error handling Extend refresh:', e))
          .finally(() => {
            // small delay before allowing another run, to avoid cascades from DOM swaps
            setTimeout(() => { window.__extendBridge_busy = false; }, 200);
          });
      };

      window.addEventListener('refreshAjaxCart', (e) => runBridge('refreshAjaxCart', e));
      window.addEventListener('refreshAjaxSideCart', (e) => runBridge('refreshAjaxSideCart', e));

      // Public, throttled refresh for theme/third-parties to force sections render immediately
      window.__triggerCartRefreshFromTheme = function(reason = 'manual') {
        const now = Date.now();
        if (window.__extendBridge_busy) return;
        if (now - window.__extendBridge_lastRun < 150) return;
        window.__extendBridge_busy = true;
        window.__extendBridge_lastRun = now;

        const cartJsonUrl = window.routes?.cart_url ? `${window.routes.cart_url}.js` : '/cart.js';
        fetch(cartJsonUrl)
          .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
          .then((freshCart) => {
            const instances = Array.from(document.querySelectorAll('cart-items, cart-drawer-items'));
            instances.forEach(inst => {
              if (typeof inst.updateCartDisplayState === 'function') inst.updateCartDisplayState(freshCart);
            });
            return Promise.all(instances.map(inst => {
              if (typeof inst.renderSections === 'function') return inst.renderSections(freshCart);
              return Promise.resolve();
            })).then(() => freshCart);
          })
          .then((freshCart) => {
            const currentPubSub = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : { cartUpdate: 'cart:update' };
            document.dispatchEvent(new CustomEvent(String(currentPubSub.cartUpdate), { bubbles: true, detail: { cart: freshCart, cartData: freshCart, source: 'theme-trigger', reason } }));

            // Let Extend re-init, but mark as theme to avoid the bridge consuming it again
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('refreshAjaxCart', { detail: { by: 'theme', reason } }));
              window.dispatchEvent(new CustomEvent('refreshAjaxSideCart', { detail: { by: 'theme', reason } }));
            }, 120);
          })
          .catch(e => console.error('[__triggerCartRefreshFromTheme] error:', e))
          .finally(() => setTimeout(() => { window.__extendBridge_busy = false; }, 180));
      };
    }
    // ===== end singleton bridge install =====
  }

  cartUpdateUnsubscriber = undefined;

  _getContextIdentifier() {
    const originalElementId = this.id;
    const originalTagName = this.tagName;
    let determinedContext = `cart-context-unknown-${originalElementId || originalTagName || 'no-id'}`;

    const isCartDrawerContext = this.cartDrawer &&
      (this.closest('cart-drawer') === this.cartDrawer ||
       this.id === 'CartDrawer-CartItems' ||
       (this.tagName && this.tagName.toUpperCase() === 'CART-DRAWER-ITEMS') ||
       (this.parentElement && this.parentElement.closest('cart-drawer')));

    let isMainCartPageContext = false;
    if (!isCartDrawerContext) {
      if (this.classList.contains('section-template--18086531760206__cart-items-padding')) {
        isMainCartPageContext = true;
      } else {
        const mainCartItemsDiv = document.getElementById('main-cart-items');
        if (mainCartItemsDiv && this.contains(mainCartItemsDiv) && this.tagName === 'CART-ITEMS') {
          isMainCartPageContext = true;
        }
      }
    }

    if (isCartDrawerContext) determinedContext = 'cart-context-drawer';
    else if (isMainCartPageContext) determinedContext = 'cart-context-main';
    else {
      if (this.id && (this.id.includes('Drawer') || this.id.includes('drawer'))) determinedContext = 'cart-context-drawer';
      else if (this.tagName && this.tagName.toUpperCase().includes('DRAWER')) determinedContext = 'cart-context-drawer';
      else if (this.id === 'main-cart-items' && this.tagName === 'CART-ITEMS') determinedContext = 'cart-context-main';
    }
    return determinedContext;
  }

  connectedCallback() {
    const cbContext = this._getContextIdentifier ? this._getContextIdentifier() : `UNKNOWN (this.id=${this.id},this.tagName=${this.tagName})`;
    console.log(`[connectedCallback - ${cbContext}] Method EXECUTION STARTED. Element:`, this);

    const subscribeFn = typeof subscribe === 'function' ? subscribe : (eventName, callback) => {
      console.warn(`[connectedCallback - ${cbContext}] Global 'subscribe' missing. Using fallback; may not work as expected.`);
      return () => {};
    };
    const currentPubSubEvents = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : { cartUpdate: 'cart:update' };
    const eventToSubscribeTo = currentPubSubEvents.cartUpdate;

    const eventHandler = (event) => {
      const handlerContext = this._getContextIdentifier ? this._getContextIdentifier() : 'UNKNOWN_HANDLER_INSTANCE';
      let cartData = null;
      if (event.detail) {
        if (event.detail.cartData && typeof event.detail.cartData.item_count !== 'undefined') cartData = event.detail.cartData;
        else if (event.detail.cart && typeof event.detail.cart.item_count !== 'undefined') cartData = event.detail.cart;
        else if (typeof event.detail.item_count !== 'undefined') cartData = event.detail;
      } else if (event.cart && typeof event.cart.item_count !== 'undefined') cartData = event.cart;

      if (cartData && typeof cartData.item_count !== 'undefined') {
        this.updateCartDisplayState(cartData);
        const shouldCallOnCartUpdate = event.source !== handlerContext || cartData.item_count === 0;
        if (shouldCallOnCartUpdate) this.onCartUpdate(cartData);
        else if (cartData.item_count > 0) this.updateAllComparePrices();
      } else {
        const cartJsonUrl = window.routes?.cart_url ? `${window.routes.cart_url}.js` : '/cart.js';
        fetch(cartJsonUrl)
          .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
          .then(currentCart => { this.updateCartDisplayState(currentCart); this.onCartUpdate(currentCart); })
          .catch(e => console.error(`[Event Listener - ${handlerContext}] Error fetching fresh cart:`, e));
      }
    };

    try {
      this.cartUpdateUnsubscriber = subscribeFn(eventToSubscribeTo, eventHandler.bind(this));
    } catch (e) {
      console.error(`[connectedCallback - ${cbContext}] Error during subscription:`, e);
    }
  }

  disconnectedCallback() {
    const context = typeof this._getContextIdentifier === 'function' ? this._getContextIdentifier() : `UNKNOWN (id: ${this.id})`;
    console.log(`[disconnectedCallback - ${context}] Method EXECUTION STARTED. Element:`, this);

    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    if (this.observer) this.observer.disconnect();

    if (this.cartDrawer) {
      if (this._drawerOpenHandlerRef) this.cartDrawer.removeEventListener('open', this._drawerOpenHandlerRef);
      if (this._drawerCloseHandlerRef) this.cartDrawer.removeEventListener('close', this._drawerCloseHandlerRef);
    }
    this._drawerListenersAdded = false;
    this._drawerOpenHandlerRef = null;
    this._drawerCloseHandlerRef = null;
  }

  // ---------- Display state / observer utilities ----------
  updateCartDisplayState(cartData) {
    const itemCount = (cartData && typeof cartData.item_count !== 'undefined') ? parseInt(cartData.item_count, 10) : 0;
    const currentContext = this._getContextIdentifier();
    if (currentContext === 'cart-context-drawer' && this.cartDrawer) {
      const cartDrawerElement = this.cartDrawer;
      const drawerInner = cartDrawerElement.querySelector('.drawer__inner');
      if (!drawerInner) return;
      const drawerHeader = drawerInner.querySelector('.drawer__header');
      const cartDrawerItemsCustomEl = drawerInner.querySelector('cart-drawer-items') || drawerInner.querySelector('cart-items');
      const cartFooter = drawerInner.querySelector('.drawer__footer');
      let emptyCartContainer = drawerInner.querySelector('.drawer__inner-empty');

      if (itemCount === 0) {
        cartDrawerElement.classList.add('is-empty');
        if (drawerHeader) drawerHeader.style.display = 'none';
        if (cartDrawerItemsCustomEl) cartDrawerItemsCustomEl.style.display = 'none';
        if (cartFooter) cartFooter.style.display = 'none';
        if (!emptyCartContainer) {
          emptyCartContainer = document.createElement('div'); emptyCartContainer.className = 'drawer__inner-empty';
          const referenceNode = drawerHeader || drawerInner.firstChild;
          if (referenceNode && referenceNode.parentNode === drawerInner) drawerInner.insertBefore(emptyCartContainer, referenceNode.nextSibling);
          else drawerInner.prepend(emptyCartContainer);
        }
        emptyCartContainer.style.display = 'block';
        if (!emptyCartContainer.querySelector('.cart-drawer__empty-content')) {
          emptyCartContainer.innerHTML = this.getEmptyCartHTMLFallback();
        }
      } else {
        cartDrawerElement.classList.remove('is-empty');
        if (drawerHeader) drawerHeader.style.display = '';
        if (cartDrawerItemsCustomEl) cartDrawerItemsCustomEl.style.display = '';
        if (cartFooter) cartFooter.style.display = '';
        if (emptyCartContainer) emptyCartContainer.style.display = 'none';
      }
    } else if (currentContext === 'cart-context-main') {
      const mainCartRootElement = this;
      const mainCartFooterElement = document.getElementById('main-cart-footer');
      if (itemCount === 0) {
        if (mainCartRootElement) mainCartRootElement.classList.add('is-empty');
        if (mainCartFooterElement) mainCartFooterElement.classList.add('is-empty');
        const formContents = mainCartRootElement ? mainCartRootElement.querySelector('.cart__contents') : null;
        if (formContents) formContents.style.display = 'none';
        const cartWarningsElement = mainCartRootElement ? mainCartRootElement.querySelector('.cart__warnings') : null;
        if (cartWarningsElement) cartWarningsElement.style.display = 'block';
      } else {
        if (mainCartRootElement) mainCartRootElement.classList.remove('is-empty');
        if (mainCartFooterElement) mainCartFooterElement.classList.remove('is-empty');
        const formContents = mainCartRootElement ? mainCartRootElement.querySelector('.cart__contents') : null;
        if (formContents) formContents.style.display = '';
        const cartWarningsElement = mainCartRootElement ? mainCartRootElement.querySelector('.cart__warnings') : null;
        if (cartWarningsElement) cartWarningsElement.style.display = 'none';
      }
    }
    document.dispatchEvent(new CustomEvent('global:cartcount:update', { detail: { count: itemCount } }));
  }

  getEmptyCartHTMLFallback() {
    return `
      <div class="cart-drawer__warnings center">
        <div class="cart-drawer__empty-content">
          <h2 class="cart__empty-text" style="display: block;">Your cart is empty</h2>
          <button class="drawer__close" type="button" onclick="const cd = this.closest('cart-drawer') || document.getElementById('CartDrawer'); if(cd && typeof cd.close === 'function') cd.close(); else console.warn('Could not find cart-drawer to close from empty message.');" aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-close" fill="none" viewBox="0 0 18 17"><path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor"></path></svg>
          </button>
          <a href="${(window.routes && window.routes.all_products_collection_url) || '/collections/all'}" class="button">Continue shopping</a>
        </div>
      </div>`;
  }

  initObserver() {
    if (!this._getContextIdentifier) return;
    const contextIdentifier = this._getContextIdentifier();
    let containerToObserve = null;
    if (contextIdentifier === 'cart-context-drawer' && this.cartDrawer) {
      containerToObserve = this.cartDrawer.querySelector('#CartDrawer-CartItems .js-contents') || this.cartDrawer.querySelector('#CartDrawer-CartItems') || this.cartDrawer.querySelector('.drawer__inner');
    } else if (contextIdentifier === 'cart-context-main') {
      const mainCartItemsEl = document.getElementById('main-cart-items');
      if (mainCartItemsEl && this.contains(mainCartItemsEl)) containerToObserve = mainCartItemsEl.querySelector('.js-contents');
    }
    if (containerToObserve) {
      this.observer = new MutationObserver(() => { this.updateAllComparePrices(); });
    }
  }

  addCartDrawerListeners() {
    const currentContext = typeof this._getContextIdentifier === 'function' ? this._getContextIdentifier() : `UNKNOWN_CONTEXT`;
    if (this.cartDrawer && currentContext === 'cart-context-drawer') {
      if (this._drawerListenersAdded) return;

      this._drawerOpenHandlerRef = () => {
        const cartJsonUrl = window.routes?.cart_url ? `${window.routes.cart_url}.js` : '/cart.js';
        if (typeof this.enableObserver === 'function') this.enableObserver();
        fetch(cartJsonUrl)
          .then(res => res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`)))
          .then(freshCartData => {
            if (typeof this.updateCartDisplayState === 'function') this.updateCartDisplayState(freshCartData);
            if (typeof this.onCartUpdate === 'function') this.onCartUpdate(freshCartData);
          })
          .catch(e => console.error(`[CartDrawer Listener - open] Error:`, e));
      };

      if (!this._drawerCloseHandlerRef) {
        this._drawerCloseHandlerRef = () => { if (typeof this.disableObserver === 'function') this.disableObserver(); };
      }

      this.cartDrawer.addEventListener('open', this._drawerOpenHandlerRef);
      this.cartDrawer.addEventListener('close', this._drawerCloseHandlerRef);
      this._drawerListenersAdded = true;
    }
  }

  enableObserver() {
    if (!this.observer || !this._getContextIdentifier) return;
    const contextIdentifier = this._getContextIdentifier();
    let containerToObserve = null;
    if (contextIdentifier === 'cart-context-drawer' && this.cartDrawer) {
      containerToObserve = this.cartDrawer.querySelector('#CartDrawer-CartItems .js-contents') || this.cartDrawer.querySelector('#CartDrawer-CartItems') || this.cartDrawer.querySelector('.drawer__inner');
    } else if (contextIdentifier === 'cart-context-main') {
      const mainCartItemsEl = document.getElementById('main-cart-items');
      if (mainCartItemsEl && this.contains(mainCartItemsEl)) containerToObserve = mainCartItemsEl.querySelector('.js-contents');
    }
    if (containerToObserve) {
      try {
        this.observer.observe(containerToObserve, { childList: true, subtree: true, attributes: true, attributeFilter: ['value', 'class', 'style'] });
      } catch (e) { console.error(`[CartItems (${contextIdentifier})] Error enabling observer:`, e); }
    }
  }

  disableObserver() { if (this.observer) this.observer.disconnect(); }

  updateAllComparePrices() {
    const currentContext = typeof this._getContextIdentifier === 'function' ? this._getContextIdentifier() : 'UNKNOWN';
    let contextElement = null;
    if (currentContext === 'cart-context-drawer' && this.cartDrawer) {
      contextElement = this.cartDrawer.querySelector('#CartDrawer-CartItems') || this.cartDrawer;
    } else if (currentContext === 'cart-context-main') {
      contextElement = this;
    } else {
      contextElement = this.querySelector('.cart-item') ? this : document;
    }
    if (!contextElement) return;

    const cartLineItems = contextElement.querySelectorAll('.cart-item');
    cartLineItems.forEach((lineItemElement) => {
      const quantityInput = lineItemElement.querySelector('input.quantity__input');
      if (quantityInput) {
        const quantity = parseInt(quantityInput.value, 10);
        if (!isNaN(quantity) && quantity > 0) {
          const comparePriceLocation1 = lineItemElement.querySelector('.cart-item__totals .cart-item__price-wrapper span.price span.compare');
          if (comparePriceLocation1) this.updateComparePrice(comparePriceLocation1, quantity);
          const comparePriceLocation2 = lineItemElement.querySelector('div.cart-item__discounted-prices s.cart-item__old-price.price.price--end span.compare');
          if (comparePriceLocation2) this.updateComparePrice(comparePriceLocation2, quantity);
        }
      }
    });
  }

  updateComparePrice(comparePriceElement, quantity) {
    const priceString = comparePriceElement.dataset.originalPrice ||
                        comparePriceElement.dataset.unitPrice ||
                        comparePriceElement.dataset.price;
    if (priceString) {
      const cleanedPriceString = String(priceString).replace(/[, ]/g, '');
      const unitPriceInDollars = parseFloat(cleanedPriceString);
      if (!isNaN(unitPriceInDollars)) {
        const totalPriceInDollars = unitPriceInDollars * quantity;
        const activeCurrency = (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active) ? Shopify.currency.active : 'USD';
        const currentLocale = document.documentElement.lang || 'en-US';
        try {
          comparePriceElement.textContent = new Intl.NumberFormat(currentLocale, { style: 'currency', currency: activeCurrency }).format(totalPriceInDollars);
        } catch {
          comparePriceElement.textContent = `${activeCurrency} ${totalPriceInDollars.toFixed(2)}`;
        }
      }
    }
  }

  // START: ADD THIS ENTIRE NEW METHOD
  renderSections(cartData = null) {
    const sectionsToUpdate = this.getSectionsToRender();
    if (!sectionsToUpdate || sectionsToUpdate.length === 0) return Promise.resolve();

    const renderPromises = sectionsToUpdate.map(sectionDetail => {
      const cartPageUrl = (window.routes && window.routes.cart_url) || '/cart';
      const fetchUrl = `${cartPageUrl}?section_id=${sectionDetail.section}&v=${new Date().getTime()}`;

      return fetch(fetchUrl)
        .then(response => {
          if (!response.ok) throw new Error(`Fetch failed for section ${sectionDetail.section}`);
          return response.text();
        })
        .then(responseText => {
          const targetElement = document.getElementById(sectionDetail.id);
          if (!targetElement) return;

          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceElement = html.querySelector(sectionDetail.selector) || html.body.firstElementChild;
          if (sourceElement) {
            const contentWrapper = targetElement.querySelector(sectionDetail.selector) || targetElement;
            contentWrapper.innerHTML = sourceElement.innerHTML;
          }
        });
    });

    return Promise.all(renderPromises)
      .then(() => {
        // After rendering, update the cart's visual state (e.g., empty message) and prices.
        const cartJsonUrl = window.routes?.cart_url ? `${window.routes.cart_url}.js` : '/cart.js';
        return fetch(cartJsonUrl)
          .then(r => r.json())
          .then(newCartData => {
            // Update all instances' display state
            document.querySelectorAll('cart-items, cart-drawer-items').forEach(inst => {
                if(typeof inst.updateCartDisplayState === 'function') inst.updateCartDisplayState(newCartData);
                if(typeof inst.updateAllComparePrices === 'function') inst.updateAllComparePrices();
            });
            // Finally, notify Extend that the cart has changed.
            try {
              window.dispatchEvent(new CustomEvent('refreshAjaxCart', { detail: { by: 'theme', reason: 'sections-rendered' }}));
              window.dispatchEvent(new CustomEvent('refreshAjaxSideCart', { detail: { by: 'theme', reason: 'sections-rendered' }}));
            } catch (e) { console.warn('[renderSections] Could not dispatch Extend refresh events:', e); }
          });
      })
      .catch(error => {
        console.error('[renderSections] Error during update:', error);
      });
  }
  // END: ADD THIS ENTIRE NEW METHOD

  onChange(event) {
    if (event.target.classList.contains('quantity__input')) {
      this.updateQuantity(
        event.target.dataset.index,
        event.target.value,
        (document.activeElement ? document.activeElement.getAttribute('name') : null) || (event.target.name || ''),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onCartUpdate(cartData = null) {
      // This function is now just a lightweight trigger for the main bridge.
      // The bridge handles all fetching, section rendering, and throttling.
      if (typeof window.__triggerCartRefreshFromTheme === 'function') {
          // We add a small delay to allow multiple events in the same browser tick
          // to be coalesced by the bridge's single, central throttle.
          setTimeout(() => {
              const context = typeof this._getContextIdentifier === 'function' ? this._getContextIdentifier() : 'unknown';
              window.__triggerCartRefreshFromTheme(`onCartUpdate:${context}`);
          }, 50);
      } else {
          console.warn('Global cart refresh trigger not found.');
      }
      return Promise.resolve(); // Maintain the original function signature
  }

  getSectionsToRenderOnPage() { return this.getSectionsToRender(); }

  updateQuantity(line, quantity, name, variantId) {
    if (!line || typeof quantity === 'undefined') return;
    this.enableLoading(line);

    const sectionsToRender = this.getSectionsToRender();
    const sectionNamesForAjax = sectionsToRender.map((section) => section.section).filter(Boolean).filter((v,i,self)=>self.indexOf(v)===i);
    if (sectionNamesForAjax.length === 0) { this.disableLoading(line); return; }

    const body = JSON.stringify({ line, quantity, sections: sectionNamesForAjax, sections_url: window.location.pathname });
    const currentFetchConfig = typeof fetchConfig === 'function' ? fetchConfig() : { headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }};
    const cartChangeUrl = (window.routes && window.routes.cart_change_url) || '/cart/change.js';

    fetch(cartChangeUrl, { ...currentFetchConfig, ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        const currentPubSubEvents = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : { cartUpdate: 'cart:update' };
        const THEME_CART_UPDATE_EVENT_NAME = String(currentPubSubEvents.cartUpdate);

        if (parsedState.sections) {
          sectionsToRender.forEach((sectionDetail) => {
            if (parsedState.sections[sectionDetail.section]) {
              const elementToReplace = document.getElementById(sectionDetail.id);
              if (elementToReplace) {
                const targetContentWrapper = elementToReplace.querySelector(sectionDetail.selector) || elementToReplace;
                targetContentWrapper.innerHTML = this.getSectionInnerHTML(parsedState.sections[sectionDetail.section], sectionDetail.selector);
              }
            }
          });
        }

        document.dispatchEvent(new CustomEvent(THEME_CART_UPDATE_EVENT_NAME, { bubbles: true, detail: { cart: parsedState, cartData: parsedState, source: this._getContextIdentifier() } }));

        if (parsedState.errors) {
          const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`) || this.querySelector(`[data-index="${line}"] input[type="number"]`);
          if (quantityElement && quantityElement.getAttribute('value')) quantityElement.value = quantityElement.getAttribute('value');
          if (this.lineItemStatusElement) { this.lineItemStatusElement.textContent = parsedState.errors; this.lineItemStatusElement.setAttribute('aria-hidden', 'false'); }
        } else {
          if (this.lineItemStatusElement) { this.lineItemStatusElement.textContent = ''; this.lineItemStatusElement.setAttribute('aria-hidden', 'true'); }
        }

        if (parsedState.item_count > 0) this.updateAllComparePrices();
        else this.updateCartDisplayState(parsedState);
      })
      .catch((e) => console.error('[CartItems] Error in updateQuantity fetch:', e))
      .finally(() => { this.disableLoading(line); });
  }

  // Base sections
  getSectionsToRender() {
    const sections = [];
    const sectionConfigs = [
        { id: 'main-cart-items', selector: '.js-contents' },
        { id: 'main-cart-footer', selector: '.js-contents', fallbackName: 'cart-footer' },
        { id: 'cart-icon-bubble', selector: '.shopify-section', fallbackName: 'cart-icon-bubble' },
        { id: 'CartDrawer', selector: '.drawer__inner', fallbackName: 'cart-drawer' }
    ];

    sectionConfigs.forEach(config => {
        const element = document.getElementById(config.id);
        if (!element) return;

        const sectionName = element.dataset.id || element.dataset.sectionId || (config.id === 'CartDrawer' ? element.getAttribute('section-id') : null) || config.fallbackName;

        if (sectionName) {
            sections.push({
                id: config.id,
                section: sectionName,
                selector: config.selector
            });
        }
    });

    return sections;
  }

  enableLoading(line) {
    const currentContext = typeof this._getContextIdentifier === 'function' ? this._getContextIdentifier() : '';
    let containerElement = (currentContext === 'cart-context-main') ? this : ((currentContext === 'cart-context-drawer' && this.cartDrawer) ? (this.cartDrawer.querySelector('#CartDrawer-CartItems') || this.cartDrawer) : this);
    if (containerElement) containerElement.classList.add('cart__items--disabled');
    const activeElementContext = (currentContext === 'cart-context-drawer' && this.cartDrawer) ? this.cartDrawer : this;
    const itemLoadingSpinner = activeElementContext.querySelector(`#CartItem-${line} .loading__spinner, #CartDrawer-Item-${line} .loading__spinner, [data-index="${line}"] .loading__spinner`);
    if (itemLoadingSpinner) itemLoadingSpinner.classList.remove('hidden');
    if (document.activeElement && typeof document.activeElement.blur === 'function') document.activeElement.blur();
    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', 'false');
  }

  disableLoading(line) {
    const currentContext = typeof this._getContextIdentifier === 'function' ? this._getContextIdentifier() : '';
    let containerElement = (currentContext === 'cart-context-main') ? this : ((currentContext === 'cart-context-drawer' && this.cartDrawer) ? (this.cartDrawer.querySelector('#CartDrawer-CartItems') || this.cartDrawer) : this);
    if (containerElement) containerElement.classList.remove('cart__items--disabled');
    const activeElementContext = (currentContext === 'cart-context-drawer' && this.cartDrawer) ? this.cartDrawer : this;
    const itemLoadingSpinner = activeElementContext.querySelector(`#CartItem-${line} .loading__spinner, #CartDrawer-Item-${line} .loading__spinner, [data-index="${line}"] .loading__spinner`);
    if (itemLoadingSpinner) itemLoadingSpinner.classList.add('hidden');
  }

  getSectionInnerHTML(html, selector) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const element = selector ? parsed.querySelector(selector) : (parsed.body.firstElementChild || parsed.body);
    if (element) return element.innerHTML;
    return parsed.body.innerHTML;
  }
}
customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();
        const debounceFn = typeof debounce === 'function' ? debounce : (fn, wait) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn.apply(this,a), wait);}};
        const currentFetchConfig = typeof fetchConfig === 'function' ? fetchConfig() : { headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }};
        const cartUpdateUrl = (window.routes && window.routes.cart_update_url) || '/cart/update.js';
        this.addEventListener(
          'input',
          debounceFn((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(cartUpdateUrl, { ...currentFetchConfig, method: 'POST', body });
          }, (typeof ON_CHANGE_DEBOUNCE_TIMER !== 'undefined' ? ON_CHANGE_DEBOUNCE_TIMER : 300))
        );
      }
    }
  );
}

// Fallbacks
(function() {
  if (typeof window.PUB_SUB_EVENTS === 'undefined') {
    window.PUB_SUB_EVENTS = { cartUpdate: 'cart:update', quantityUpdate: 'quantity:update', variantChange: 'variant:change' };
  }
  if (typeof window.routes === 'undefined') {
    window.routes = { cart_url: '/cart', cart_change_url: '/cart/change.js', cart_update_url: '/cart/update.js', all_products_collection_url: '/collections/all', product_recommendations_url: '/recommendations/products' };
  }
  if (typeof window.fetchConfig === 'undefined') {
    window.fetchConfig = () => ({ headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }});
  }
  if (typeof window.debounce !== 'function') {
    window.debounce = function(fn, wait) { let timeout; return function(...args) { const context = this; clearTimeout(timeout); timeout = setTimeout(() => fn.apply(context, args), wait); }; };
  }
  if (typeof window.subscribe !== 'function') {
    const subscribers = {};
    window.subscribe = function(eventName, callback) {
      if (!subscribers[eventName]) {
        subscribers[eventName] = { cbs: new Set(), wired: false };
      }
      const bucket = subscribers[eventName];
      bucket.cbs.add(callback);

      if (!bucket.wired) {
        bucket.wired = true;
        document.addEventListener(String(eventName), (evt) => {
          bucket.cbs.forEach(cb => {
            try { cb(evt); } catch (e) { console.error('[subscribe] cb error', e); }
          });
        });
      }
      return function unsubscribe() { bucket.cbs.delete(callback); };
    };
  }
  if (typeof window.ON_CHANGE_DEBOUNCE_TIMER === 'undefined') {
    window.ON_CHANGE_DEBOUNCE_TIMER = 300;
  }
})();


// --- Fallback: ensure Extend drawer offers also render on /cart (Extend sidecart script opts out there) ---
(function installExtendDrawerFallbackOnCartPage() {
  // Only on /cart
  if (!location.pathname.includes('/cart')) return;

  // Idempotent
  if (window.__extendDrawerFallbackInstalled) return;
  window.__extendDrawerFallbackInstalled = true;

  // Wait for Extend SDK
  function waitForExtendReady(retries = 40) {
    return new Promise((resolve, reject) => {
      (function tick(remaining) {
        const ok = window.Extend && window.ExtendShopify && window.Extend.buttons && typeof window.Extend.buttons.renderSimpleOffer === 'function';
        if (ok) return resolve();
        if (remaining <= 0) return reject(new Error('Extend SDK not ready'));
        setTimeout(() => tick(remaining - 1), 100);
      })(retries);
    });
  }

  async function renderDrawerOffersOnce() {
    try {
      await waitForExtendReady();

      // Fresh cart
      const cart = await fetch('/cart.js', { headers:{'Accept':'application/json'} }).then(r=>r.json());

      const rows = document.querySelectorAll('cart-drawer-items .cart-item');
      if (!rows.length) return;

      const regEx = /\d+$/;
      rows.forEach((row, idx) => {
        try {
          const titleA = row.querySelector('.cart-item__name');
          if (!titleA) return;

          // Skip existing warranty lines
          if ((titleA.textContent || '').toLowerCase().includes('extend protection')) return;

          // Container to append offer into
          const container = row.querySelector('.cart-item__details');
          if (!container) return;

          // Quantity
          const qtyInput = row.querySelector('input[name="updates[]"], input.quantity__input');
          const quantity = qtyInput ? String(qtyInput.value || '1') : '1';

          // Variant id (from href or cart fallback)
          const href = titleA.getAttribute('href') || '';
          const fromUrl = (href.match(regEx) || [])[0];
          const variantId = fromUrl || String((cart.items[idx] && cart.items[idx].id) || '');

          if (!variantId) return;

          // Avoid duplicate button
          if (row.querySelector('.extend-side-cart-offer[data-extend-variant="'+variantId+'"]')) return;

          // Create placeholder node for Extend
          const mount = document.createElement('div');
          mount.className = 'extend-side-cart-offer';
          mount.setAttribute('data-extend-variant', variantId);
          mount.setAttribute('data-extend-quantity', quantity);
          container.appendChild(mount);

          // Product data for pricing/category
          const cartItem = cart.items.find(it => String(it.id) === String(variantId)) || cart.items[idx];
          const productCategory = cartItem ? cartItem.product_type : undefined;
          const productPrice = cartItem ? parseInt(cartItem.price, 10) : undefined;

          // Already has an Extend instance?
          if (window.Extend.buttons.instance(mount)) return;

          // Render the offer button
          window.Extend.buttons.renderSimpleOffer(mount, {
            referenceId: variantId,
            price: productPrice,
            category: productCategory,
            onAddToCart: function (options) {
              window.ExtendShopify.addPlanToCart({
                plan: options.plan,
                product: options.product,
                quantity: quantity
              }, function (err) {
                if (err) {
                  console.error('[Extend drawer fallback] addPlanToCart error:', err);
                  return;
                }
                // Keep everything in sync after add
                try {
                  window.dispatchEvent(new CustomEvent('refreshAjaxSideCart', { detail: { by: 'theme', reason: 'extend-drawer-fallback' }}));
                  window.dispatchEvent(new CustomEvent('refreshAjaxCart', { detail: { by: 'theme', reason: 'extend-drawer-fallback' }}));
                } catch {}
              });
            }
          });
        } catch (e) {
          console.warn('[Extend drawer fallback] Row error:', e);
        }
      });
    } catch (e) {
      // Silent: if SDK isn’t ready or drawer not visible yet, we’ll try again on next drawer open
      // console.debug('[Extend drawer fallback] skipped:', e);
    }
  }

  // Hook into drawer openings (cart.js already emits <cart-drawer> "open")
  const drawer = document.querySelector('cart-drawer');
  if (drawer) {
    drawer.addEventListener('open', () => {
      // Give our theme a moment to finish rendering drawer sections, then render offers
      setTimeout(renderDrawerOffersOnce, 120);
    });
  }
})();

/* === Extend Drawer Offer Shim v3 (robust on /cart; reattaches observers after swaps) === */
(function extendDrawerOfferShim_v3() {
  if (window.__extendDrawerOfferShim_v3) return;
  window.__extendDrawerOfferShim_v3 = true;

  const cartUpdateEvt = (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) || 'cart:update';
  const onCartPage = location.pathname.includes('/cart');

  const debounce = (fn, wait = 160) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn.apply(null, a), wait); }; };

  function waitForExtendReady(retries = 80, delay = 100) {
    return new Promise((resolve, reject) => {
      (function tick(left) {
        const ok = window.Extend && window.ExtendShopify && window.Shopify.currency.active === 'USD' &&
          window.Extend.buttons && typeof window.Extend.buttons.renderSimpleOffer === 'function' &&
          typeof window.ExtendShopify.addPlanToCart === 'function' &&
          typeof window.ExtendShopify.warrantyAlreadyInCart === 'function';
        if (ok) return resolve();
        if (left <= 0) return reject(new Error('Extend SDK not ready'));
        setTimeout(() => tick(left - 1), delay);
      })(retries);
    });
  }

  async function getCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(r => r.json());
  }

  function coveredVariantSet(cart) {
    const set = new Set();
    (cart.items || []).forEach(it => {
      const p = it.properties || {};
      const covered = p['_Extend.ProductId'] || p['_extend_product_id'] || p['_extend.productId'];
      if (covered) set.add(String(covered));
    });
    return set;
  }

  function getRowInfo(row) {
    const nameA = row.querySelector('.cart-item__name');
    if (!nameA) return null;
    const title = (nameA.textContent || '').toLowerCase();
    if (title.includes('extend protection')) return null; // skip warranty rows
    const qtyInput = row.querySelector('input[name="updates[]"], input.quantity__input');
    const quantity = qtyInput ? String(qtyInput.value || '1') : '1';
    const fromData = qtyInput && qtyInput.getAttribute('data-quantity-variant-id');
    if (fromData) return { variantId: String(fromData), quantity };
    const href = nameA.getAttribute('href') || '';
    const m = href.match(/\d+$/);
    return { variantId: m ? String(m[0]) : '', quantity };
  }

  function ensurePlaceholder(row, cls, variantId, quantity) {
    let node = row.querySelector('.' + cls);
    if (!node) {
      node = document.createElement('div');
      node.className = cls;
      const container = row.querySelector('.cart-item__details') || row;
      container.appendChild(node);
    }
    node.setAttribute('data-extend-variant', variantId);
    node.setAttribute('data-extend-quantity', quantity);
    return node;
  }

  async function renderDrawerOffers() {
    try {
      await waitForExtendReady();
      const cart = await getCart();
      const covered = coveredVariantSet(cart);

      const rows = document.querySelectorAll('cart-drawer-items .cart-item');
      if (!rows.length) return;

      rows.forEach((row) => {
        try {
          const info = getRowInfo(row);
          if (!info || !info.variantId) return;

          const { variantId, quantity } = info;

          // If covered, remove any button for this row; DO NOT touch other rows
          if (covered.has(String(variantId))) {
            const existing = row.querySelector('.extend-side-cart-offer');
            if (existing) existing.remove();
            return;
          }

          // Ensure mount exists & is current
          const mount = ensurePlaceholder(row, 'extend-side-cart-offer', variantId, quantity);

          // Skip if Extend already owns this mount
          if (window.Extend.buttons.instance && window.Extend.buttons.instance(mount)) return;

          const cartItem = cart.items.find(it => String(it.id) === String(variantId));
          const productCategory = cartItem ? cartItem.product_type : undefined;
          const productPrice = cartItem ? parseInt(cartItem.price, 10) : undefined;

          window.Extend.buttons.renderSimpleOffer(mount, {
            referenceId: variantId,
            price: productPrice,
            category: productCategory,
            onAddToCart: function (options) {
              window.ExtendShopify.addPlanToCart({
                plan: options.plan,
                product: options.product,
                quantity: quantity
              }, function (err) {
                if (err) { console.error('[Extend Shim v3] addPlanToCart error:', err); return; }
                if (typeof window.__triggerCartRefreshFromTheme === 'function') {
                  window.__triggerCartRefreshFromTheme('extend-shim-add');
                } else {
                  window.dispatchEvent(new CustomEvent('refreshAjaxSideCart', { detail: { by:'theme', reason:'extend-shim-add' }}));
                  window.dispatchEvent(new CustomEvent('refreshAjaxCart', { detail: { by:'theme', reason:'extend-shim-add' }}));
                }
              });
            }
          });
        } catch (rowErr) {
          console.warn('[Extend Shim v3] Row error:', rowErr);
        }
      });
    } catch {
      /* quiet retry via triggers below */
    }
  }

  const debouncedRender = debounce(renderDrawerOffers, 140);

  // --- Reattach a MutationObserver to the CURRENT drawer container after every swap ---
  let mo = null;
  let observedEl = null;
  function attachDrawerObserver() {
    const current = document.querySelector('#CartDrawer-CartItems');
    if (!current || current === observedEl) return;
    if (mo) mo.disconnect();
    observedEl = current;
    mo = new MutationObserver(() => debouncedRender());
    mo.observe(current, { childList: true, subtree: true });
  }

  // Triggers: run twice (short + longer delay) to catch normalize and async swaps on /cart
  function scheduleRehydrate(reason = '') {
    attachDrawerObserver();
    setTimeout(renderDrawerOffers, onCartPage ? 220 : 140);
    setTimeout(renderDrawerOffers, onCartPage ? 520 : 280);
  }

  window.__extendDrawerShimV3_rehydrate = scheduleRehydrate;

  window.addEventListener('refreshAjaxSideCart', () => scheduleRehydrate('refreshAjaxSideCart'));
  window.addEventListener('refreshAjaxCart',     () => scheduleRehydrate('refreshAjaxCart'));
  document.addEventListener(cartUpdateEvt,        () => scheduleRehydrate('pubsub:cart:update'));

  const drawer = document.querySelector('cart-drawer');
  if (drawer) drawer.addEventListener('open', () => scheduleRehydrate('drawer:open'));

  // Also react to direct remove/qty clicks (helpful on /cart)
  document.addEventListener('click', (e) => {
    if (e.target.closest('cart-remove-button') || e.target.closest('.quantity__button')) {
      // Let the theme/Shopify update first, then rehydrate
      setTimeout(() => {
        if (typeof window.__triggerCartRefreshFromTheme === 'function') {
          window.__triggerCartRefreshFromTheme('extend-shim-click');
        }
        scheduleRehydrate('click');
      }, 40);
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    attachDrawerObserver();
    setTimeout(renderDrawerOffers, 300);
    if (onCartPage) setTimeout(renderDrawerOffers, 650);
  });
})();